import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Clock,
  FlaskConical,
  Loader2,
  MinusCircle,
  Play,
  Plus,
  Save,
  SkipForward,
  Square,
  Trash2,
  XCircle,
} from 'lucide-react';
import { RootState } from '../store';
import { useToast } from '../components/Toast';
import {
  createInstance,
  getTestCase,
  getInstance,
  IssueSeverity,
  StepResultStatus,
  TEST_ACTORS,
  TestCase,
  TestCaseInstance,
  TestEnvironment,
  TestInstanceStatus,
  updateInstance,
} from '../services/testCasesApi';
import { DateInput } from '../components/DateInput';
import { TimeInput } from '../components/TimeInput';

const STEP_STATUS_OPTIONS: { value: StepResultStatus; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'not_run', label: 'Non testé', icon: <MinusCircle className="w-4 h-4" />, color: 'text-gray-400' },
  { value: 'passed', label: 'Réussi', icon: <CheckCircle2 className="w-4 h-4" />, color: 'text-green-600' },
  { value: 'failed', label: 'Échoué', icon: <XCircle className="w-4 h-4" />, color: 'text-red-600' },
  { value: 'skipped', label: 'Ignoré', icon: <SkipForward className="w-4 h-4" />, color: 'text-gray-400' },
];

function computeStatus(stepResults: { status: StepResultStatus }[]): TestInstanceStatus {
  if (stepResults.length === 0) return 'NOT_RUN';
  const failed = stepResults.some((s) => s.status === 'failed');
  const allPassed = stepResults.every((s) => s.status === 'passed');
  const anyRun = stepResults.some((s) => s.status === 'passed' || s.status === 'failed');
  if (allPassed) return 'PASSED';
  if (failed && anyRun) return 'PARTIAL';
  if (failed) return 'FAILED';
  return 'IN_PROGRESS';
}

export function TestCaseInstanceFormPage() {
  const { testCaseId, instanceId } = useParams<{ testCaseId?: string; instanceId?: string }>();
  const isEdit = Boolean(instanceId);
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);

  const [tc, setTc] = useState<TestCase | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const startRef = useRef<string>(new Date().toTimeString().slice(0, 5));

  const [executorName, setExecutorName] = useState((user as any)?.name ?? '');
  const [executorRole, setExecutorRole] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState(startRef.current);
  const [endTime, setEndTime] = useState('');
  const [environment, setEnvironment] = useState<TestEnvironment>('LOCAL');
  const [status, setStatus] = useState<TestInstanceStatus>('NOT_RUN');
  const [stepResults, setStepResults] = useState<{ stepOrder: number; status: StepResultStatus; notes: string }[]>([]);
  const [issues, setIssues] = useState<{
    stepOrder?: number | null;
    severity: IssueSeverity;
    description: string;
    jiraTicket: string;
    markerIoReportId: string;
  }[]>([]);
  const [generalNotes, setGeneralNotes] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        if (isEdit && instanceId) {
          const inst = await getInstance(instanceId);
          const tcData = await getTestCase(inst.testCaseId);
          setTc(tcData);
          setExecutorName(inst.executorName ?? '');
          setExecutorRole(inst.executorRole ?? '');
          setDate(inst.date ?? new Date().toISOString().split('T')[0]);
          setStartTime(inst.startTime ?? '');
          setEndTime(inst.endTime ?? '');
          setEnvironment(inst.environment);
          setStatus(inst.status);
          setStepResults(inst.stepResults.map((sr) => ({ ...sr, notes: sr.notes ?? '' })));
          setIssues(inst.issues.map((i) => ({
            ...i,
            jiraTicket: i.jiraTicket ?? '',
            markerIoReportId: i.markerIoReportId ?? '',
          })));
          setGeneralNotes(inst.generalNotes ?? '');
        } else if (testCaseId) {
          const tcData = await getTestCase(testCaseId);
          setTc(tcData);
          setStepResults(tcData.steps.map((s) => ({ stepOrder: s.order, status: 'not_run' as StepResultStatus, notes: '' })));
        }
      } catch (err: any) {
        showToast(err.message || 'Erreur de chargement', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [testCaseId, instanceId, isEdit]);

  const updateStepResult = (idx: number, field: 'status' | 'notes', value: string) => {
    const next = stepResults.map((sr, i) => i === idx ? { ...sr, [field]: value } : sr);
    setStepResults(next);
    setStatus(computeStatus(next));
  };

  const addIssue = () => {
    setIssues((prev) => [...prev, { severity: 'major', description: '', jiraTicket: '', markerIoReportId: '', stepOrder: null }]);
  };

  const updateIssue = (idx: number, field: string, value: unknown) => {
    setIssues((prev) => prev.map((is, i) => i === idx ? { ...is, [field]: value } : is));
  };

  const removeIssue = (idx: number) => {
    setIssues((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFinish = async (finalStatus?: TestInstanceStatus) => {
    if (!tc) return;
    const endT = new Date().toTimeString().slice(0, 5);
    setEndTime(endT);
    const resolvedStatus = finalStatus ?? status;
    setStatus(resolvedStatus);

    try {
      setSaving(true);
      const payload: Partial<TestCaseInstance> = {
        testCaseId: tc.id,
        executorName: executorName || undefined,
        executorRole: executorRole || undefined,
        date,
        startTime,
        endTime: endT,
        environment,
        status: resolvedStatus,
        stepResults: stepResults.map((sr) => ({ ...sr, notes: sr.notes || undefined })),
        issues: issues.map((i) => ({
          ...i,
          stepOrder: i.stepOrder ?? undefined,
          jiraTicket: i.jiraTicket || undefined,
          markerIoReportId: i.markerIoReportId || undefined,
        })),
        generalNotes: generalNotes || undefined,
      };

      if (isEdit && instanceId) {
        await updateInstance(instanceId, payload);
        showToast('Exécution mise à jour', 'success');
        navigate(`/test-case-instances/${instanceId}`);
      } else {
        const inst = await createInstance(payload);
        showToast('Exécution enregistrée', 'success');
        navigate(`/test-case-instances/${inst.id}`);
      }
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!tc) {
    return <div className="text-center py-20 text-gray-500">Cas de test introuvable.</div>;
  }

  const actorName = TEST_ACTORS.find((a) => a.id === tc.primaryActor)?.label ?? tc.primaryActor;

  const statusLabel =
    status === 'NOT_RUN' ? 'Non exécuté'
    : status === 'IN_PROGRESS' ? 'En cours'
    : status === 'PASSED' ? 'Réussi ✓'
    : status === 'FAILED' ? 'Échoué ✗'
    : status === 'PARTIAL' ? 'Partiel'
    : 'Bloqué';

  const statusColor =
    status === 'PASSED' ? 'text-green-700 bg-green-50 border-green-200'
    : status === 'FAILED' ? 'text-red-700 bg-red-50 border-red-200'
    : status === 'PARTIAL' ? 'text-orange-700 bg-orange-50 border-orange-200'
    : status === 'BLOCKED' ? 'text-purple-700 bg-purple-50 border-purple-200'
    : 'text-gray-600 bg-gray-50 border-gray-200';

  return (
    <div className="space-y-4">
      <ToastComponent />

      {/* ── Header bar ── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(`/test-cases/${tc.id}`)} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="min-w-0">
            <p className="text-xs text-gray-400 font-mono">{tc.moduleId} · Acteur : {actorName}</p>
            <h1 className="text-xl font-display font-bold text-textPrimary flex items-center gap-2 truncate">
              <Play className="w-5 h-5 text-green-600 flex-shrink-0" />
              {isEdit ? 'Modifier l\'exécution' : 'Nouvelle exécution'}
              <span className="text-gray-400 font-normal text-base truncate">— {tc.name}</span>
            </h1>
          </div>
        </div>
        {/* Sticky save actions in header */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold ${statusColor}`}>
            {status === 'PASSED' && <CheckCircle2 className="w-3.5 h-3.5" />}
            {status === 'FAILED' && <XCircle className="w-3.5 h-3.5" />}
            {status === 'IN_PROGRESS' && <Clock className="w-3.5 h-3.5" />}
            {statusLabel}
          </span>
          <button type="button" onClick={() => navigate(`/test-cases/${tc.id}`)} className="btn-secondary text-sm">
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleFinish('BLOCKED')}
            disabled={saving}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-1.5 text-sm"
          >
            <Square className="w-3.5 h-3.5" />
            Bloquer
          </button>
          <button
            type="button"
            onClick={() => void handleFinish()}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>

      {/* ── Session info — compact horizontal strip ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Testeur</label>
            <input
              value={executorName}
              onChange={(e) => setExecutorName(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="Nom du testeur"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Rôle dans l'app</label>
            <div className="relative">
              <select
                value={executorRole}
                onChange={(e) => setExecutorRole(e.target.value)}
                className="w-full pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Sélectionner</option>
                {TEST_ACTORS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Environnement</label>
            <div className="relative">
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as TestEnvironment)}
                className="w-full pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="LOCAL">Local</option>
                <option value="STAGING">Staging</option>
                <option value="PRODUCTION">Production</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
            <DateInput  
              value={date}
              onChange={setDate}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Début</label>
            <TimeInput
              value={startTime}
              onChange={setStartTime}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fin</label>
            <TimeInput
              value={endTime}
              onChange={setEndTime}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* ── Main body: steps (left 2/3) + sidebar (right 1/3) ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

        {/* Step results — main column */}
        <div className="xl:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800">Résultats par étape</h2>

          {tc.steps.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Ce cas de test n'a pas d'étapes définies.</p>
          ) : (
            <div className="space-y-3">
              {tc.steps.map((step, idx) => {
                const sr = stepResults[idx] ?? { stepOrder: step.order, status: 'not_run', notes: '' };
                return (
                  <div
                    key={step.order}
                    className={`border rounded-lg p-4 transition-colors ${
                      sr.status === 'passed' ? 'border-green-200 bg-green-50'
                      : sr.status === 'failed' ? 'border-red-200 bg-red-50'
                      : sr.status === 'skipped' ? 'border-gray-100 bg-gray-50'
                      : 'border-gray-200'
                    }`}
                  >
                    {/* Step header */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-white border border-gray-300 text-xs font-bold rounded-full flex-shrink-0">
                        {step.order}
                      </span>
                      <div className="flex gap-1 ml-auto flex-wrap">
                        {STEP_STATUS_OPTIONS.map((o) => (
                          <button
                            key={o.value}
                            type="button"
                            onClick={() => updateStepResult(idx, 'status', o.value)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                              sr.status === o.value
                                ? 'border-current bg-white shadow-sm'
                                : 'border-transparent bg-white/60 hover:bg-white'
                            } ${o.color}`}
                          >
                            {o.icon} {o.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Action + expected side by side */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Action</p>
                        <p className="text-gray-800 leading-snug">{step.action}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Résultat attendu</p>
                        <p className="text-gray-600 leading-snug">{step.expectedResult}</p>
                      </div>
                    </div>

                    {/* Notes full width */}
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Observations / écarts</p>
                      <input
                        value={sr.notes}
                        onChange={(e) => updateStepResult(idx, 'notes', e.target.value)}
                        placeholder="Renseigner si résultat différent de l'attendu..."
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right sidebar: incidents + notes + status */}
        <div className="xl:col-span-1 space-y-4">

          {/* Incidents */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-800">Incidents & bugs</h2>
              <button type="button" onClick={addIssue} className="btn-secondary inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5">
                <Plus className="w-3.5 h-3.5" />
                Ajouter
              </button>
            </div>

            {issues.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-xs border-2 border-dashed rounded-lg">
                Aucun incident enregistré.
              </div>
            ) : (
              <div className="space-y-3">
                {issues.map((issue, idx) => (
                  <div key={idx} className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-xs font-semibold text-orange-800">Incident {idx + 1}</span>
                      </div>
                      <button type="button" onClick={() => removeIssue(idx)} className="text-gray-400 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Sévérité</label>
                        <div className="relative">
                          <select
                            value={issue.severity}
                            onChange={(e) => updateIssue(idx, 'severity', e.target.value)}
                            className="w-full pl-2 pr-7 py-1.5 border border-gray-300 rounded-lg text-xs appearance-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                          >
                            <option value="blocker">Bloquant</option>
                            <option value="major">Majeur</option>
                            <option value="minor">Mineur</option>
                            <option value="cosmetic">Cosmétique</option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Étape</label>
                        <div className="relative">
                          <select
                            value={issue.stepOrder ?? ''}
                            onChange={(e) => updateIssue(idx, 'stepOrder', e.target.value ? Number(e.target.value) : null)}
                            className="w-full pl-2 pr-7 py-1.5 border border-gray-300 rounded-lg text-xs appearance-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                          >
                            <option value="">Général</option>
                            {tc.steps.map((s) => (
                              <option key={s.order} value={s.order}>Étape {s.order}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
                      <textarea
                        value={issue.description}
                        onChange={(e) => updateIssue(idx, 'description', e.target.value)}
                        rows={2}
                        placeholder="Décrivez l'incident observé..."
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-primary-500 focus:border-primary-500 bg-white resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Jira</label>
                        <input
                          value={issue.jiraTicket}
                          onChange={(e) => updateIssue(idx, 'jiraTicket', e.target.value)}
                          placeholder="DTS-147"
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-primary-500 focus:border-primary-500 bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marker.io</label>
                        <input
                          value={issue.markerIoReportId}
                          onChange={(e) => updateIssue(idx, 'markerIoReportId', e.target.value)}
                          placeholder="mio-abc123"
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-primary-500 focus:border-primary-500 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* General notes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">Notes générales</h2>
            <textarea
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              rows={5}
              placeholder="Observations globales, contexte particulier, recommandations..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>

          {/* Status + actions (sticky bottom of sidebar) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-3">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Statut calculé automatiquement</p>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold ${statusColor}`}>
                {status === 'PASSED' && <CheckCircle2 className="w-4 h-4" />}
                {status === 'FAILED' && <XCircle className="w-4 h-4" />}
                {status === 'IN_PROGRESS' && <Clock className="w-4 h-4" />}
                {statusLabel}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => void handleFinish()}
                disabled={saving}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer l'exécution
              </button>
              <button
                type="button"
                onClick={() => void handleFinish('BLOCKED')}
                disabled={saving}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center justify-center gap-2 text-sm"
              >
                <Square className="w-4 h-4" />
                Marquer comme bloqué
              </button>
              <button type="button" onClick={() => navigate(`/test-cases/${tc.id}`)} className="btn-secondary w-full">
                Annuler
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
